import { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Typography } from 'app/components';
import { i18n } from 'app/utils/i18n';
import { TagItemShortcuts, TagAction } from '../../TagItemShortcuts/TagItemShortcuts';
import { useFindOrCreateTagContext, useUpdateTagContext } from 'app/api/tag-queries/tag-contexts';
import { useAddContextToConfig, useRemoveContextFromConfig, useSetActiveContext } from 'app/api/tag-queries/document-taxonomy-config';
import { DocumentTaxonomyConfigContext } from 'app/api/tag-queries/types';
import { DateDefault } from '../types/taxonomy-settings';
import { AddDateModal, AffectedTagsModal } from './modals';
import Tippy from '@tippyjs/react';

import './Section.scss';

type DateInput = {
  type: 'period' | 'instant' | 'forever';
  fromDate?: Date;
  toDate?: Date;
  date?: Date;
};

interface DateDefaultsSectionProps {
  configId: string;
  documentSuperId: number;
  contextSelections: DocumentTaxonomyConfigContext[];
}

interface DeleteTarget {
  id: string;
  label: string;
  count: number;
}

export const DateDefaultsSection = ({ configId, documentSuperId, contextSelections }: DateDefaultsSectionProps) => {
  const [showDateModal, setShowDateModal] = useState(false);
  const [editDateData, setEditDateData] = useState<DateDefault | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const history = useHistory();
  const location = useLocation();

  const { mutateAsync: findOrCreateTagContext } = useFindOrCreateTagContext();
  const { mutate: updateTagContext } = useUpdateTagContext();
  const { mutateAsync: addContextToConfig } = useAddContextToConfig();
  const { mutate: removeContextFromConfig } = useRemoveContextFromConfig();
  const { mutate: setActiveContext } = useSetActiveContext();

  // Derive display data from contextSelections
  const dateDefaults: DateDefault[] = useMemo(
    () =>
      contextSelections.map((cs) => {
        const ctx = cs.context;
        const count = cs.tagCount ?? 0;
        if (ctx?.periodForever) {
          return { id: cs.contextId, type: 'forever' as const, isActive: cs.isActive ?? false, referencedTags: count };
        }
        if (ctx?.periodInstant) {
          return {
            id: cs.contextId,
            type: 'instant' as const,
            isActive: cs.isActive ?? false,
            referencedTags: count,
            date: new Date(ctx.periodInstant).toISOString(),
          };
        }
        return {
          id: cs.contextId,
          type: 'period' as const,
          isActive: cs.isActive ?? false,
          referencedTags: count,
          fromDate: ctx?.periodStartDate ? new Date(ctx.periodStartDate).toISOString() : undefined,
          toDate: ctx?.periodEndDate ? new Date(ctx.periodEndDate).toISOString() : undefined,
        };
      }),
    [contextSelections]
  );

  const handleSetPrimary = (contextId: string) => {
    setActiveContext({ configId, contextId });
  };

  const handleDeleteClick = (dateDefault: DateDefault) => {
    if (dateDefault.referencedTags > 0) {
      setDeleteTarget({ id: dateDefault.id, label: getDateDisplay(dateDefault), count: dateDefault.referencedTags });
    } else {
      removeContextFromConfig({ configId, contextId: dateDefault.id });
    }
  };

  const handleGoToTags = () => {
    if (!deleteTarget) return;
    const overviewUrl = location.pathname.replace(/\/taxonomies$/, '');
    history.push(overviewUrl, {
      tagFilter: { type: 'context', value: deleteTarget.id, label: deleteTarget.label },
    });
    setDeleteTarget(null);
  };

  const handleAdd = async (dateData: DateInput) => {
    if (!configId) return;

    const hasForeverDate = contextSelections.some((cs) => cs.context?.periodForever);
    if (dateData.type === 'forever' && hasForeverDate) {
      return;
    }

    const context = await findOrCreateTagContext({
      documentSuperId,
      periodStartDate: dateData.type === 'period' && dateData.fromDate ? dateData.fromDate : null,
      periodEndDate: dateData.type === 'period' && dateData.toDate ? dateData.toDate : null,
      periodInstant: dateData.type === 'instant' && dateData.date ? dateData.date : null,
      periodForever: dateData.type === 'forever' ? true : undefined,
    });

    const alreadyInConfig = contextSelections.some((cs) => cs.contextId === context.id);
    if (!alreadyInConfig) {
      await addContextToConfig({ configId, contextId: context.id });
    }
  };

  const handleEdit = (contextId: string, dateData: DateInput) => {
    updateTagContext({
      tagContextId: contextId,
      documentSuperId,
      periodStartDate: dateData.type === 'period' && dateData.fromDate ? dateData.fromDate : null,
      periodEndDate: dateData.type === 'period' && dateData.toDate ? dateData.toDate : null,
      periodInstant: dateData.type === 'instant' && dateData.date ? dateData.date : null,
      periodForever: dateData.type === 'forever' ? true : undefined,
    });
    setEditDateData(null);
  };

  const handleOpenEdit = (dateDefault: DateDefault) => {
    setEditDateData(dateDefault);
    setShowDateModal(true);
  };

  const handleOpenAdd = () => {
    setEditDateData(null);
    setShowDateModal(true);
  };

  const handleCloseModal = () => {
    setShowDateModal(false);
    setEditDateData(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  const getDateDisplay = (dateDefault: DateDefault) => {
    if (dateDefault.type === 'period' && dateDefault.fromDate && dateDefault.toDate) {
      return `${formatDate(dateDefault.fromDate)} → ${formatDate(dateDefault.toDate)}`;
    }
    if (dateDefault.type === 'instant' && dateDefault.date) {
      return formatDate(dateDefault.date);
    }
    if (dateDefault.type === 'forever') {
      return i18n('tag.forever.no.constraint');
    }
    return '';
  };

  const getTypeLabel = (type: 'period' | 'instant' | 'forever') => {
    const labels = {
      period: i18n('tag.period.title'),
      instant: i18n('tag.instant.label'),
      forever: i18n('tag.forever.label'),
    };
    return labels[type];
  };

  return (
    <section className="defaults-section">
      <div className="defaults-section__header">
        <Typography variant="body-8" fontWeight="semibold">
          {i18n('tag.date.label')} {i18n('tag.default.label')}
        </Typography>
        <button className="custom-btn" onClick={handleOpenAdd}>
          <i className="fa-regular fa-plus" />
          {i18n('common.add')} {i18n('tag.date.label')}
        </button>
      </div>

      {dateDefaults.length === 0 ? (
        <div className="empty-state">
          <Typography variant="body-7" color="#6c757d">
            {i18n('tag.date.empty')}
          </Typography>
        </div>
      ) : (
        <div className="defaults-list">
          {[...dateDefaults]
            .sort((a, b) => Number(b.isActive) - Number(a.isActive))
            .map((dateDefault) => {
              const actions: TagAction[] = [{ type: 'delete', onClick: () => handleDeleteClick(dateDefault) }];

              if (dateDefault.type !== 'forever') {
                actions.unshift({ type: 'edit', onClick: () => handleOpenEdit(dateDefault) });
              }

              if (!dateDefault.isActive) {
                actions.unshift({ type: 'default', onClick: () => handleSetPrimary(dateDefault.id) });
              }

              return (
                <div key={dateDefault.id} className={`default-item ${dateDefault.isActive ? 'default-item--primary' : ''}`}>
                  <div className="default-item__main">
                    <div className="default-item__indicator">
                      {dateDefault.isActive && (
                        <Tippy
                          content={
                            <Typography variant="body-5" color="#6c757d" className="defaults-section__tippy-content">
                              {i18n('tag.primary.date')}
                            </Typography>
                          }
                        >
                          <i className="fa-solid fa-star fa-xs" />
                        </Tippy>
                      )}
                    </div>

                    <div className="default-item__content">
                      <Typography variant="body-7" fontWeight="semibold">
                        {getTypeLabel(dateDefault.type)}
                      </Typography>
                      <Typography variant="body-7" color="#6c757d">
                        {getDateDisplay(dateDefault)}
                      </Typography>
                    </div>
                  </div>

                  <div className="default-item__meta">
                    <Typography variant="body-5" color="#547BA2">
                      {dateDefault.referencedTags} {i18n('tag.active.tags')}
                    </Typography>
                    <TagItemShortcuts actions={actions} />
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <AddDateModal
        show={showDateModal}
        editData={
          editDateData
            ? {
                id: editDateData.id,
                type: editDateData.type,
                fromDate: editDateData.fromDate,
                toDate: editDateData.toDate,
                date: editDateData.date,
              }
            : undefined
        }
        hasForeverDate={dateDefaults.some((d) => d.type === 'forever')}
        onClose={handleCloseModal}
        onAdd={handleAdd}
        onEdit={handleEdit}
      />

      <AffectedTagsModal
        show={!!deleteTarget}
        affectedCount={deleteTarget?.count ?? 0}
        itemLabel={deleteTarget?.label ?? ''}
        onClose={() => setDeleteTarget(null)}
        onGoToTags={handleGoToTags}
      />
    </section>
  );
};
